import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill, RowActions } from "../components/UI";
import { useAuth } from "../lib/auth";
import { can } from "../lib/roles";
import SearchableSelect from "../components/SearchableSelect";

const STAGES = ["draft", "sent", "follow up", "won", "lost", "waitlist"];
const STAGE_TONE = {
  "draft": "neutral", "sent": "blue", "follow up": "amber",
  "won": "green", "lost": "red", "waitlist": "maroon",
};

function NewQuoteDialog({ onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [f, setF] = useState({ customer_id: "", machine_id: "", catalogue_item_id: "", weekly_rate: 2400,
    transport_charge: "", transport_out_charge: "", transport_provider: "", po_number: "",
    site_location: "", status: "draft",
    wanted_capacity_tonnes: "", wanted_by_date: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCustName, setNewCustName] = useState("");
  const [downOnBackdrop, setDownOnBackdrop] = useState(false);

  // /machines is Director/Office/Read-only (server.py:133). For Accounts it
  // 403s, and a bare Promise.all would reject and leave the page blank. The
  // machine picker is optional - quotes are built from catalogue items - so
  // soft-fail to an empty list rather than re-opening fleet data to Accounts.
  const load = () => Promise.all([
    api.get("/customers"),
    api.get("/machines").catch(e => {
      if (e?.response?.status === 403) return { data: [] };
      throw e;
    }),
    api.get("/catalogue"),
  ]).then(([c, m, cat]) => {
    setCustomers(c.data); setMachines(m.data); setCatalogue(cat.data.items || []);
  });
  useEffect(() => { load(); }, []);

  const createInlineProspect = async () => {
    if (!newCustName.trim()) return;
    try {
      const r = await api.post("/customers", { legal_name: newCustName.trim(), is_prospect: true });
      await load();
      setF(prev => ({ ...prev, customer_id: r.data.customer_id }));
      setShowNewCust(false); setNewCustName("");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const monthly = Math.round(Number(f.weekly_rate || 0) * 4.33 * 100) / 100;
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...f, weekly_rate: Number(f.weekly_rate) };
      if (!payload.transport_charge) delete payload.transport_charge; else payload.transport_charge = Number(payload.transport_charge);
      if (!payload.transport_out_charge) delete payload.transport_out_charge; else payload.transport_out_charge = Number(payload.transport_out_charge);
      if (!payload.transport_provider) delete payload.transport_provider;
      if (!payload.po_number) delete payload.po_number;
      if (!payload.machine_id) delete payload.machine_id;
      if (!payload.catalogue_item_id) delete payload.catalogue_item_id;
      if (!payload.wanted_capacity_tonnes) delete payload.wanted_capacity_tonnes; else payload.wanted_capacity_tonnes = Number(payload.wanted_capacity_tonnes);
      if (!payload.wanted_by_date) delete payload.wanted_by_date;
      await api.post("/quotes", payload);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const backdropDown = (e) => setDownOnBackdrop(e.target === e.currentTarget);
  const backdropUp = (e) => {
    if (downOnBackdrop && e.target === e.currentTarget) onClose();
    setDownOnBackdrop(false);
  };
  return (
    <div className="ach-modal-backdrop" data-testid="new-quote-dialog" onMouseDown={backdropDown} onMouseUp={backdropUp}>
      <div className="ach-modal" style={{maxWidth: 720}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0,fontSize:16,fontWeight:600}}>New quote</h2>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}}>Close</button>
        </div>
        <div className="ach-modal__body">
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Customer</label>
              <div style={{display:"flex", gap:8, alignItems:"stretch"}}>
                <div style={{flex:1}}>
                  <SearchableSelect
                    testid="q-customer"
                    value={f.customer_id}
                    onChange={(v)=>setF({...f, customer_id: v})}
                    placeholder="— pick one —"
                    options={customers.map(c => ({
                      value: c.id,
                      label: c.legal_name + (c.is_prospect ? " (prospect)" : ""),
                      sub: c.abn ? `ABN ${c.abn}` : "no ABN yet",
                    }))}
                  />
                </div>
                <button type="button" className="ach-btn ach-btn--grey" style={{padding:"0 12px", fontSize:12}} onClick={()=>setShowNewCust(true)} data-testid="q-add-customer">+ New</button>
              </div>
              {showNewCust && (
                <div style={{marginTop:8, padding:10, background:"var(--ach-page-bg)", borderRadius:6, display:"flex", gap:8, alignItems:"end"}} data-testid="q-inline-newcust">
                  <div style={{flex:1}}>
                    <label style={{fontSize:11, color:"var(--ach-text-2)"}}>Prospect name (ABN not needed yet)</label>
                    <input value={newCustName} onChange={(e)=>setNewCustName(e.target.value)} placeholder="e.g. ACME Civil Pty Ltd" data-testid="q-newcust-name" />
                  </div>
                  <button className="ach-btn ach-btn--primary" style={{padding:"6px 12px", fontSize:12}} onClick={createInlineProspect} data-testid="q-newcust-save">Add prospect</button>
                  <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>{setShowNewCust(false); setNewCustName("");}}>Cancel</button>
                </div>
              )}
            </div>
            <div className="ach-field"><label>Machine (optional at quote stage)</label>
              <SearchableSelect
                testid="q-machine"
                value={f.machine_id}
                onChange={(v)=>setF({...f, machine_id: v, catalogue_item_id: v ? "" : f.catalogue_item_id})}
                placeholder="— TBC —"
                options={machines.map(m => ({
                  value: m.id,
                  label: `${m.unit_number} · ${m.make} ${m.model_name} · ${m.capacity_tonnes}t`,
                  sub: `Serial ${m.serial || "—"}`,
                }))}
              />
            </div>
            {catalogue.length > 0 && (
              <div className="ach-field"><label>…or a quote crane (not on our fleet)</label>
                <select data-testid="q-catalogue" value={f.catalogue_item_id}
                  onChange={(e)=>{
                    const chosen = catalogue.find(x => x.id === e.target.value);
                    setF({...f, catalogue_item_id: e.target.value, machine_id: e.target.value ? "" : f.machine_id,
                      weekly_rate: chosen && chosen.default_weekly_rate ? chosen.default_weekly_rate : f.weekly_rate});
                  }}>
                  <option value="">— none —</option>
                  {catalogue.map(c => <option key={c.id} value={c.id}>{c.category}{c.sub_category ? " · " + c.sub_category : ""} · {c.description}</option>)}
                </select>
                <div className="helper">Not part of the fleet — appears only on this quote.</div>
              </div>
            )}
            <div className="ach-field"><label>Weekly rate ($)</label>
              <input data-testid="q-weekly" type="number" value={f.weekly_rate} onChange={(e)=>setF({...f, weekly_rate: e.target.value})} />
              <div className="helper">Monthly worked out: <strong>${monthly.toLocaleString()}</strong> + GST</div>
            </div>
            <div className="ach-field"><label>Transport in (one-off, $)</label>
              <input data-testid="q-transport-in" type="number" value={f.transport_charge} onChange={(e)=>setF({...f, transport_charge: e.target.value})} />
            </div>
            <div className="ach-field"><label>Transport out (one-off, $)</label>
              <input data-testid="q-transport-out" type="number" value={f.transport_out_charge} onChange={(e)=>setF({...f, transport_out_charge: e.target.value})} />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Transport provider (who was used / quoted)</label>
              <input data-testid="q-transport-provider" value={f.transport_provider} onChange={(e)=>setF({...f, transport_provider: e.target.value})} placeholder="e.g. AJP Heavy Haulage" />
            </div>
            <div className="ach-field"><label>Customer PO number</label>
              <input data-testid="q-po" value={f.po_number} onChange={(e)=>setF({...f, po_number: e.target.value})} placeholder="e.g. PO-45129" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Site / location</label>
              <input data-testid="q-site" value={f.site_location} onChange={(e)=>setF({...f, site_location: e.target.value})} />
            </div>
            <div className="ach-field"><label>Stage</label>
              <select data-testid="q-stage" value={f.status} onChange={(e)=>setF({...f, status: e.target.value})}>
                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {f.status === "waitlist" && (
              <>
                <div className="ach-field"><label>Wanted capacity (tonnes)</label>
                  <input data-testid="q-cap" type="number" value={f.wanted_capacity_tonnes} onChange={(e)=>setF({...f, wanted_capacity_tonnes: e.target.value})} />
                </div>
                <div className="ach-field"><label>Wanted by</label>
                  <input data-testid="q-by" type="date" value={f.wanted_by_date} onChange={(e)=>setF({...f, wanted_by_date: e.target.value})} />
                </div>
              </>
            )}
            {err && <div style={{color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer">
          <button className="ach-btn ach-btn--primary" onClick={submit} disabled={busy || !f.customer_id} data-testid="q-save">
            {busy ? "Saving…" : "Save quote"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditQuoteDialog — added for Friction Batch (c), Sep 2026.
// Todd's ask: an Edit button on existing quotes. The 10 fields below are
// exactly the whitelist PATCH /quotes/{id} accepts (server.py:5172); anything
// outside that list is deliberately not touchable here. Machine and customer
// are intentionally not editable — a quote for a different customer is a new
// quote.
// The chains-formatting question (structured line vs. notes) is separate —
// affects how Todd types values, not whether this dialog exists.
// ---------------------------------------------------------------------------
function EditQuoteDialog({ quote, onClose, onSaved }) {
  const [f, setF] = useState({
    status:                 quote.status || "draft",
    weekly_rate:            quote.weekly_rate ?? "",
    transport_charge:       quote.transport_charge ?? "",
    transport_out_charge:   quote.transport_out_charge ?? "",
    transport_provider:     quote.transport_provider || "",
    po_number:              quote.po_number || "",
    site_location:          quote.site_location || "",
    notes:                  quote.notes || "",
    wanted_capacity_tonnes: quote.wanted_capacity_tonnes ?? "",
    wanted_by_date:         quote.wanted_by_date || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    setBusy(true); setErr("");
    try {
      // Numbers → Number, blanks → null so we don't overwrite a set value with "".
      const numify = (v) => (v === "" || v == null ? null : Number(v));
      const payload = {
        status:                 f.status,
        weekly_rate:            numify(f.weekly_rate),
        transport_charge:       numify(f.transport_charge),
        transport_out_charge:   numify(f.transport_out_charge),
        transport_provider:     f.transport_provider || null,
        po_number:              f.po_number || null,
        site_location:          f.site_location || null,
        notes:                  f.notes || null,
        wanted_capacity_tonnes: numify(f.wanted_capacity_tonnes),
        wanted_by_date:         f.wanted_by_date || null,
      };
      // Strip nulls so the server-side "$set: v" doesn't overwrite an existing
      // value with null (PATCH treats absence as "leave alone" — that's the
      // right behaviour until the $unset fix lands as Batch item 5).
      Object.keys(payload).forEach(k => payload[k] == null && delete payload[k]);
      await api.patch(`/quotes/${quote.id}`, payload);
      onSaved(); onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="edit-quote-dialog" onMouseDown={(e)=>{ if (e.target === e.currentTarget) onClose(); }}>
      <div className="ach-modal" style={{maxWidth: 720}} onMouseDown={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0, fontSize:16, fontWeight:600}}>Edit quote — {quote.number}</h2>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}} data-testid="eq-close">Close</button>
        </div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Update the money, the site, the PO and the stage. If the customer or the machine on the quote is wrong, start a new quote — those are not editable here.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>Weekly rate ($)</label>
              <input type="number" min="0" step="50" value={f.weekly_rate}
                     onChange={(e)=>set("weekly_rate", e.target.value)} data-testid="eq-weekly" />
            </div>
            <div className="ach-field">
              <label>Stage</label>
              <select value={f.status} onChange={(e)=>set("status", e.target.value)} data-testid="eq-stage">
                {STAGES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="ach-field">
              <label>Transport in ($)</label>
              <input type="number" min="0" step="50" value={f.transport_charge}
                     onChange={(e)=>set("transport_charge", e.target.value)} data-testid="eq-transport-in" />
            </div>
            <div className="ach-field">
              <label>Transport out ($)</label>
              <input type="number" min="0" step="50" value={f.transport_out_charge}
                     onChange={(e)=>set("transport_out_charge", e.target.value)} data-testid="eq-transport-out" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Transport provider</label>
              <input value={f.transport_provider} onChange={(e)=>set("transport_provider", e.target.value)}
                     placeholder="e.g. AJP Heavy Haulage" data-testid="eq-transport-provider" />
            </div>
            <div className="ach-field">
              <label>PO number</label>
              <input value={f.po_number} onChange={(e)=>set("po_number", e.target.value)} data-testid="eq-po" />
            </div>
            <div className="ach-field">
              <label>Site</label>
              <input value={f.site_location} onChange={(e)=>set("site_location", e.target.value)} data-testid="eq-site" />
            </div>
            <div className="ach-field">
              <label>Wanted capacity (tonnes)</label>
              <input type="number" min="0" step="0.5" value={f.wanted_capacity_tonnes}
                     onChange={(e)=>set("wanted_capacity_tonnes", e.target.value)} data-testid="eq-cap" />
            </div>
            <div className="ach-field">
              <label>Wanted by</label>
              <input type="date" value={f.wanted_by_date}
                     onChange={(e)=>set("wanted_by_date", e.target.value)} data-testid="eq-by" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Notes</label>
              <textarea rows={3} value={f.notes} onChange={(e)=>set("notes", e.target.value)}
                        placeholder="Anything special — access, timing, chains, whatever the customer needs to know"
                        data-testid="eq-notes" />
            </div>
            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="eq-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy} data-testid="eq-save">
            {busy ? "Saving…" : "Save changes →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);  // Friction Batch (c): current quote being edited
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState(null);
  const [confirmConvert, setConfirmConvert] = useState(null);
  const [search, setSearch] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const canManage = can(user, "quotes.manage");
  // Converting a quote creates a hire, which the server does not allow for
  // Accounts (server.py:4092). Kept separate so opening the quote controls
  // to Accounts does not surface a button that 403s.
  const canConvert = can(user, "quotes.convert-to-hire");

  const showFlash = (tone, text) => {
    setFlash({ tone, text });
    setTimeout(() => setFlash(null), 4000);
  };

  const [showArchived, setShowArchived] = useState(false);

  const load = () => Promise.all([
    api.get(`/quotes${showArchived ? "?include_archived=1" : ""}`),
    api.get("/drafts?kind=quote").catch(()=>({data:{drafts:[]}})),
  ]).then(([q, d]) => {
    setQuotes(q.data.quotes);
    setDrafts(d.data.drafts || []);
  });
  useEffect(() => { load(); }, [showArchived]);

  const archiveQuote = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/archive`, {});
      showFlash("green", `Archived quote ${q.number}.`);
      await load();
    } catch (e) {
      showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const restoreQuote = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/restore`, {});
      showFlash("green", `Restored quote ${q.number}.`);
      await load();
    } catch (e) {
      showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this saved-for-later quote draft?")) return;
    await api.delete(`/drafts/${id}`);
    load();
  };

  const grouped = STAGES.reduce((acc, s) => { acc[s] = quotes.filter(q => q.status === s); return acc; }, {});
  // Won-and-not-yet-converted is what's actually "ready to become a hire".
  // Once the wizard finishes and converted_hire_id is set, the quote is done — remove it from the KPI.
  const wonReady = grouped["won"].filter(q => !q.converted_hire_id);
  const wonWeekly = wonReady.reduce((s, q) => s + Number(q.weekly_rate || 0), 0);

  // PDF button root cause (Todd's original complaint, Sep 2026): the old
  // implementation was `window.open(url, "_blank")` which opens a new
  // tab whose GET does not carry the bearer JWT (auth is in localStorage,
  // not cookies). The backend replied 401 and Edge showed a blank/error
  // tab — Todd's "nothing happens". Fix: fetch the PDF with our
  // authenticated axios instance, wrap the bytes in a blob URL, and open
  // THAT in a new tab. The blob URL is same-origin and needs no auth.
  const openPdf = async (id, number) => {
    // Open a blank tab synchronously inside the click handler — that keeps
    // the user-gesture context intact so strict popup blockers (Edge with
    // default settings, some ad-blockers) don't swallow it. Then fetch the
    // PDF with the axios instance that carries the bearer JWT, and set the
    // already-open tab's location to the resulting blob URL.
    const w = window.open("about:blank", "_blank");
    if (!w) {
      showFlash("amber", `Your browser blocked the pop-up. Allow pop-ups for this site to view ${number}.pdf.`);
      return;
    }
    try {
      const r = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      try { w.close(); } catch (_) { /* browser may already have blocked us */ }
      showFlash("red", `Couldn't open the PDF for ${number}: ${e?.response?.status === 404 ? "quote not found" : (e?.message || "unknown error")}`);
    }
  };
  const send = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/quotes/${id}/send`);
      showFlash("green", r.data.live ? "Sent live via Resend." : "Would-send logged (Resend not switched on).");
      await load();
    } catch (e) {
      showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };
  const setStage = async (id, status) => {
    await api.patch(`/quotes/${id}`, { status });
    await load();
  };
  const doConvert = async (q) => {
    setConfirmConvert(null);
    setBusy(q.id);
    try {
      const r = await api.post(`/quotes/${q.id}/convert-to-hire`);
      sessionStorage.setItem("quote-prefill", JSON.stringify(r.data.prefill || {}));
      nav("/start-a-hire");
    } catch (e) {
      showFlash("red", formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  return (
    <div data-testid="quotes-page">
      <PageHeader
        title="Quotes"
        subtitle="Pipeline board — draft to won, everything in one place"
        actions={<>
          <label style={{display:"inline-flex", alignItems:"center", gap:6, fontSize:12, marginRight:12, color:"var(--ach-text-2)"}}>
            <input type="checkbox" checked={showArchived} onChange={(e)=>setShowArchived(e.target.checked)} data-testid="quotes-show-archived" />
            Show archived
          </label>
          {canManage && <button className="ach-btn ach-btn--primary" onClick={()=>setDialog(true)} data-testid="btn-new-quote">+ New quote</button>}
        </>}
      />

      <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(4, minmax(0,1fr))"}}>
        <KpiCard tone="amber" number={grouped["draft"].length + grouped["follow up"].length} label="Needing our attention" testid="q-kpi-waiting" />
        <KpiCard tone="blue" number={grouped["sent"].length} label="Out with them" testid="q-kpi-out" />
        <KpiCard tone="green" number={wonReady.length} label="Won — ready to become hires" testid="q-kpi-won" />
        <KpiCard tone="maroon" number={money(wonWeekly).replace(".00","")} label="Weekly rate at stake · won" testid="q-kpi-weekly" />
      </div>

      <div className="ach-card" style={{marginBottom: 12, padding: "12px 16px"}} data-testid="stage-counts">
        <div style={{display:"flex", gap:10, flexWrap:"wrap", alignItems:"center"}}>
          <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:0.6, color:"var(--ach-text-2)", marginRight:4}}>By stage</div>
          {STAGES.map(s => (
            <div key={s} style={{display:"flex", alignItems:"center", gap:6}} data-testid={`stage-count-${s.replace(" ", "-")}`}>
              <StatusPill tone={STAGE_TONE[s]}>{s.toUpperCase()}</StatusPill>
              <span style={{fontSize:13, fontWeight:600, color:"var(--ach-charcoal)"}}>{grouped[s].length}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="ach-card">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, gap:8, flexWrap:"wrap"}}>
          <h2 className="ach-card__title" style={{margin:0}}>{showDrafts ? "Saved-for-later drafts" : "Live quotes"}</h2>
          <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
            <button className={`ach-btn ${showDrafts ? "ach-btn--grey" : "ach-btn--primary"}`}
              style={{padding:"6px 12px", fontSize:12}}
              onClick={()=>setShowDrafts(false)} data-testid="tab-quotes-live">Live ({quotes.length})</button>
            <button className={`ach-btn ${showDrafts ? "ach-btn--primary" : "ach-btn--grey"}`}
              style={{padding:"6px 12px", fontSize:12}}
              onClick={()=>setShowDrafts(true)} data-testid="tab-quote-drafts">Drafts ({drafts.length})</button>
            <input placeholder={showDrafts ? "Search draft title…" : "Search number, customer, machine…"} value={search} onChange={(e)=>setSearch(e.target.value)}
              data-testid="quotes-search"
              style={{border:"1px solid var(--ach-card-border)", borderRadius:6, padding:"8px 12px", fontSize:13, width:280, maxWidth:"100%", boxSizing:"border-box"}} />
          </div>
        </div>
        {showDrafts ? (
          <div className="ach-table-wrap" data-testid="quote-drafts-list">
            {drafts.filter(d => !search || (d.title||"").toLowerCase().includes(search.toLowerCase())).length === 0 ? (
              <div style={{padding:20, textAlign:"center", color:"var(--ach-text-2)", fontSize:13}}>
                No saved quote drafts.
              </div>
            ) : (
              <table className="ach-table">
                <thead><tr><th>Draft</th><th>Saved by</th><th>Updated</th><th></th></tr></thead>
                <tbody>
                  {drafts.filter(d => !search || (d.title||"").toLowerCase().includes(search.toLowerCase())).map(d => (
                    <tr key={d.id} data-testid={`draft-quote-${d.id}`}>
                      <td><div className="unit-num">{d.title}</div><div className="subtitle">saved {new Date(d.created_at).toLocaleDateString("en-AU")}</div></td>
                      <td>{d.created_by_name}</td>
                      <td>{new Date(d.updated_at).toLocaleDateString("en-AU")}</td>
                      <RowActions>
                        <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>{ setDialog(true); sessionStorage.setItem("quote-draft-resume", JSON.stringify({id: d.id, payload: d.payload})); }} data-testid={`draft-quote-resume-${d.id}`}>Resume →</button>
                        <button className="ach-btn ach-btn--ghost ach-btn--row" style={{color:"var(--ach-red)"}} onClick={()=>deleteDraft(d.id)} data-testid={`draft-quote-delete-${d.id}`}>×</button>
                      </RowActions>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Customer</th>
                <th>Machine (proposed)</th>
                <th>PO</th>
                <th>Weekly rate</th>
                <th>Stage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {quotes.filter(q => {
                if (!search) return true;
                const s = search.toLowerCase();
                return ((q.number||"").toLowerCase().includes(s) || (q.customer_name||"").toLowerCase().includes(s)
                  || (q.machine_summary||"").toLowerCase().includes(s) || (q.po_number||"").toLowerCase().includes(s));
              }).map(q => (
                <tr key={q.id} data-testid={`quote-row-${q.number}`}>
                  <td><div className="unit-num">{q.number}</div><div className="subtitle">exp {q.expires_at}</div></td>
                  <td>{q.customer_name}</td>
                  <td>{q.machine_summary}</td>
                  <td>{q.po_number || <span style={{color:"var(--ach-text-2)"}}>—</span>}</td>
                  <td>{money(q.weekly_rate)}</td>
                  <td>
                    <select value={q.status} onChange={(e)=>setStage(q.id, e.target.value)} disabled={!canManage}
                      data-testid={`stage-${q.number}`}
                      style={{border:"1px solid var(--ach-card-border)", borderRadius:6, padding:"4px 6px", fontSize:11, fontWeight:600, textTransform:"uppercase",
                        background: `var(--ach-${STAGE_TONE[q.status]}-tint, var(--ach-neutral-tint))`,
                        color: `var(--ach-${STAGE_TONE[q.status]}, var(--ach-text-2))`}}>
                      {STAGES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                    </select>
                  </td>
                  <RowActions>
                    <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>openPdf(q.id, q.number)} data-testid={`pdf-${q.number}`}>PDF</button>
                    {canManage && !q.converted_hire_id && (
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>setEditing(q)} data-testid={`edit-${q.number}`}>Edit</button>
                    )}
                    {canManage && !q.converted_hire_id && q.status !== "lost" && (
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>send(q.id)} disabled={busy === q.id} data-testid={`send-${q.number}`}>
                        {busy === q.id ? "…" : "Email"}
                      </button>
                    )}
                    {canConvert && !q.converted_hire_id && q.status !== "lost" && (
                      <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={()=>setConfirmConvert(q)} disabled={busy === q.id} data-testid={`convert-${q.number}`}>
                        {busy === q.id ? "…" : "Turn into hire →"}
                      </button>
                    )}
                    {canManage && !q.converted_hire_id && !q.archived_at && (
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>archiveQuote(q)} data-testid={`archive-${q.number}`} title="Archive this quote">
                        Archive
                      </button>
                    )}
                    {canManage && q.archived_at && (
                      <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={()=>restoreQuote(q)} data-testid={`restore-${q.number}`}>
                        Restore
                      </button>
                    )}
                    {q.converted_hire_id && (
                      <span style={{fontSize:11, color:"var(--ach-green)", fontWeight:600, alignSelf:"center"}} data-testid={`converted-${q.number}`}>✓ Converted to hire</span>
                    )}
                  </RowActions>
                </tr>
              ))}
              {quotes.length === 0 && (
                <tr><td colSpan={7} style={{textAlign:"center", padding: 24, color:"var(--ach-text-2)"}}>No live quotes.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
        <div style={{fontSize: 11, color:"var(--ach-text-2)", marginTop: 10}}>
          Emailing a quote goes through the Resend connection — until Resend is switched on, the email is logged in Emails instead of actually sent.
        </div>
      </div>

      {dialog && <NewQuoteDialog onClose={()=>setDialog(false)} onSaved={load} />}
      {editing && <EditQuoteDialog quote={editing} onClose={()=>setEditing(null)}
        onSaved={()=>{ showFlash("green", `Updated quote ${editing.number}.`); load(); }} />}

      {flash && (
        <div data-testid="quotes-flash"
          style={{position:"fixed", right:20, bottom:20, zIndex:1000, padding:"10px 14px", borderRadius:8,
            background: flash.tone === "red" ? "var(--ach-red-tint)" : flash.tone === "green" ? "var(--ach-green-tint)" : "var(--ach-neutral-tint)",
            color: flash.tone === "red" ? "var(--ach-red)" : flash.tone === "green" ? "var(--ach-green)" : "var(--ach-text-1)",
            border: "1px solid var(--ach-card-border)", fontSize:13, fontWeight:500, maxWidth:360, boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
          {flash.text}
        </div>
      )}

      {confirmConvert && (
        <div className="ach-modal-backdrop" data-testid="confirm-convert-dialog" onClick={()=>setConfirmConvert(null)}>
          <div className="ach-modal" style={{maxWidth: 480}} onClick={(e)=>e.stopPropagation()}>
            <div className="ach-modal__header">
              <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Turn quote {confirmConvert.number} into a hire?</h2>
            </div>
            <div className="ach-modal__body">
              <p style={{margin:0, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
                Stage moves to <strong>Won</strong> and the Start-a-hire wizard opens with the details pre-filled.
                The quote only shows as fully converted once you finish the wizard and the hire is created.
              </p>
            </div>
            <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button className="ach-btn ach-btn--grey" onClick={()=>setConfirmConvert(null)} data-testid="confirm-convert-cancel">Cancel</button>
              <button className="ach-btn ach-btn--primary" onClick={()=>doConvert(confirmConvert)} data-testid="confirm-convert-go">Yes, open the wizard →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
